import { useCurrentSortedAlerts } from '@backoffice-frontend/alerts-common'
import { useTheme } from '@backoffice-frontend/patterns'
import { isBookable } from '@backoffice-frontend/vehicle-common'
import { Text } from '@moia-dev/pace-web'
import { AlertAssignmentIcon } from '../AlertAssignmentIcon'
import type { VehicleListOverlayRowVehicleFragment } from './VehicleListOverlayRow.hook'

type VehicleListOverlayRowProps = {
  vehicle: VehicleListOverlayRowVehicleFragment
  onClick: () => void
  isSelected: boolean
}

export const VehicleListOverlayRow = ({
  vehicle,
  onClick,
  isSelected,
}: VehicleListOverlayRowProps) => {
  const theme = useTheme()
  const { label, taskNotifications, fccCurrentAdAlerts } = vehicle
  const sortedAlerts = useCurrentSortedAlerts(
    taskNotifications?.tasks,
    fccCurrentAdAlerts?.alerts,
  )

  return (
    <div
      role="button"
      tabIndex={-1}
      css={{
        display: 'flex',
        padding: `${theme.space.Space2} ${theme.space.Space3}`,
        alignItems: 'center',
        gap: `${theme.space.Space1}`,
        cursor: 'pointer',
        borderBottom: `1px solid ${theme.semantic.ColorBorderOnSelectedDefault}`,
        '&:last-child': {
          borderBottom: 'none',
        },
        '&:hover': {
          h6: {
            color: theme.semantic.ColorActionPrimaryAlternativeHovered,
          },
        },
        '&:focus, &:focus-visible': {
          outline: 'none',
        },
      }}
      onClick={onClick}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.space.Space2,
        }}
      >
        <ColoredDot
          color={
            isBookable(vehicle)
              ? theme.semantic.ColorSuccessDefault
              : theme.semantic.ColorCriticalDefault
          }
        />
        <Text
          weight="bold"
          size="lg"
          css={{
            width: 48,
            color: isSelected ? theme.semantic.ColorAccentDefault : undefined,
          }}
        >
          {label}
        </Text>
      </div>
      <div
        css={{
          display: 'flex',
          width: 20,
          height: 20,
          alignItems: 'center',
          gap: 10,
        }}
      >
        <AlertAssignmentIcon enableTooltip alerts={sortedAlerts} />
      </div>
    </div>
  )
}

const ColoredDot = ({ color }: { color: string }) => (
  <span
    css={{
      width: 8,
      height: 8,
      borderRadius: 100,
      background: color,
    }}
  />
)
